import { type DesignSourceEnum, InsightsApi, PlatformEnum } from '@insights/insights-data-provider'
import { queryOptions } from '@tanstack/react-query'

import { CookieConsentService } from '@inter-ikea-kompis/core/services'
import type { IDexfSettings } from '@inter-ikea-kompis/core/types'
import { initializeKompisInsights } from '@inter-ikea-kompis/core/utilities'

import { kioskIntegration } from '@/integrations/kiosk'
import { APP_ID } from '@/integrations/kompis/constants'
import { setSentryUser } from '@/integrations/sentry'
import { getCountryFromPath, getLanguageFromPath, getLocaleFromPath } from '@/utils/base-path-params'
import { getAppVersion } from '@/utils/get-app-version'

export function initAnalyticsQueryOptions(dexfSettings: IDexfSettings, computerId?: string, storeId?: string) {
  return queryOptions({
    queryFn: async () => await connectApplication(dexfSettings, computerId, storeId),
    queryKey: ['init-insights', getLocaleFromPath()],
    staleTime: Infinity,
  })
}

export function initPlanningSessionQueryOptions(
  insightsApi: InsightsApi | null,
  designId: string,
  designSource: DesignSourceEnum,
) {
  return queryOptions({
    queryFn: async () => {
      await insightsApi?.initPlanningSession(designId, designSource, {}, designId)
      return null
    },
    queryKey: ['init-planning-session'],
    staleTime: Infinity,
  })
}

async function checkCookieConsent(dexfSettings: IDexfSettings) {
  if (!import.meta.env.PROD) {
    // Cookie consent is not relevant outside of production.
    return true
  }
  if (kioskIntegration.getKioskPlatform()) return true // It is always allowed to track when in kiosk

  try {
    const service = new CookieConsentService({
      locale: getLocaleFromPath(),
      settings: dexfSettings,
    })
    const consentLevel = 2

    return await service.hasCookieConsent(consentLevel)
  } catch {
    // Default to false if there is an error.
    return false
  }
}

async function connectApplication(dexfSettings: IDexfSettings, computerId?: string, storeId?: string) {
  const hasCookieConsent = await checkCookieConsent(dexfSettings)
  const isKiosk = !!kioskIntegration.getKioskPlatform()

  if (!hasCookieConsent) return null

  const insightsApi = new InsightsApi()
  const isDev = import.meta.env.MODE !== 'production'

  await insightsApi.connectApplication({
    applicationId: APP_ID,
    applicationVersion: getAppVersion(),
    countryCode: getCountryFromPath(),
    dev: isDev,
    isPlanner: false,
    kiosk: isKiosk,
    kioskId: computerId,
    languageCode: getLanguageFromPath(),
    platform: isKiosk ? PlatformEnum.kiosk : PlatformEnum.owfe,
    storeId,
  })

  initializeKompisInsights(insightsApi, isDev)

  setSentryUser({ storeId, computerId })

  return insightsApi
}
